import axios from 'axios';

const API_PATH = process.env.REACT_APP_API_BASE_URL_LOCAL;

const defaultConfig = {
  headers: {
    Authorization: `Bearer `,
  },
  responseType: 'json',
  authorization: true,
};

class API {
  constructor() {
    this.accessToken = this.getStorageAccessToken();
    this.refreshToken = this.getStorageRefreshToken();
    this.updateTabsOpenCount();
    this.prepareUnauthorizedInterceptor();
    this.prepareAuthorizeInterceptor();
  }

  getStorageAccessToken = () => {
    let accessToken = localStorage.getItem('cec-access-token');

    // Verify if the data is in the session (unload event will erase localstorage info even if the page is refreshed)
    if (accessToken === null)
      accessToken = sessionStorage.getItem('cec-access-token');

    if (accessToken !== null)
      localStorage.setItem('cec-access-token', accessToken);

    return accessToken;
  };

  getStorageRefreshToken = () => {
    let refreshToken = localStorage.getItem('cec-refresh-token');

    // Verify if the data is in the session (unload event will erase localstorage info even if the page is refreshed)
    if (refreshToken === null)
      refreshToken = sessionStorage.getItem('cec-refresh-token');

    if (refreshToken !== null)
      localStorage.setItem('cec-refresh-token', refreshToken);

    return refreshToken;
  };

  prepareUnauthorizedInterceptor = () => {
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        if (
          error.config.url !== `${API_PATH}/refreshToken` &&
          error.response?.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          if (this.refreshToken && this.refreshToken !== '') {
            let positiveResponse = false;

            await axios
              .post(
                `${API_PATH}/refreshToken`,
                {},
                {
                  headers: {
                    Authorization: `Bearer ${this.refreshToken}`,
                  },
                }
              )
              .then((response) => {
                const { accessToken } = response.data;

                this.setInstanceTokens(accessToken, this.refreshToken);

                originalRequest.headers[
                  'Authorization'
                ] = `Bearer ${accessToken}`;

                positiveResponse = true;
              })
              .catch((err) => {
                this.refreshToken = null;
                this.accessToken = null;

                this.clearStorage();

                return Promise.reject(err);
              });

            if (positiveResponse) return axios(originalRequest);
          }
        }

        return Promise.reject(error);
      }
    );
  };

  prepareAuthorizeInterceptor = () => {
    axios.interceptors.request.use(
      (config) => {
        if (
          config.authorization !== false &&
          config.url !== `${API_PATH}/refreshToken`
        ) {
          if (this.accessToken) {
            config.headers.Authorization = `Bearer ${this.accessToken}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  };

  updateTabsOpenCount = () => {
    let tabsOpenCount = localStorage.getItem('cec-tabs-count');
    // Verify if the data exist or set it to 1 (default)
    tabsOpenCount =
      tabsOpenCount === null || isNaN(tabsOpenCount)
        ? 1
        : parseInt(tabsOpenCount) + 1;

    localStorage.setItem('cec-tabs-count', tabsOpenCount);
  };

  setInstanceTokens = (newAccessToken, newRefreshToken) => {
    this.refreshToken = newRefreshToken;
    this.accessToken = newAccessToken;

    localStorage.setItem('cec-access-token', this.accessToken);
    localStorage.setItem('cec-refresh-token', this.refreshToken);
    sessionStorage.setItem('cec-access-token', this.accessToken);
    sessionStorage.setItem('cec-refresh-token', this.refreshToken);

    return this;
  };

  onTabClosed = () => {
    let currentOpenTabsCount = parseInt(localStorage.getItem('cec-tabs-count'));

    if (currentOpenTabsCount === null || isNaN(currentOpenTabsCount))
      currentOpenTabsCount = 1;

    currentOpenTabsCount =
      currentOpenTabsCount - 1 < 0 ? 0 : currentOpenTabsCount - 1;

    if (currentOpenTabsCount === 0) {
      localStorage.removeItem('cec-access-token');
      localStorage.removeItem('cec-refresh-token');
      localStorage.removeItem('cec-tabs-count');
      localStorage.removeItem('cec-user');

      return;
    }

    localStorage.setItem('cec-tabs-count', currentOpenTabsCount);
  };

  clearStorage = () => {
    localStorage.removeItem('cec-access-token');
    localStorage.removeItem('cec-refresh-token');
    sessionStorage.removeItem('cec-access-token');
    sessionStorage.removeItem('cec-refresh-token');
  };

  get = (url, config = defaultConfig) => {
    const finalConfig = { ...defaultConfig, ...config };
    return axios.get(`${API_PATH}${url}`, finalConfig);
  };

  post = (url, data, config = defaultConfig) => {
    const finalConfig = { ...defaultConfig, ...config };
    return axios.post(`${API_PATH}${url}`, data, finalConfig);
  };

  put = (url, data, config = defaultConfig) => {
    const finalConfig = { ...defaultConfig, ...config };
    return axios.put(`${API_PATH}${url}`, data, finalConfig);
  };

  patch = (url, data, config = defaultConfig) => {
    const finalConfig = { ...defaultConfig, ...config };
    return axios.patch(`${API_PATH}${url}`, data, finalConfig);
  };

  delete = (url, config = defaultConfig) => {
    const finalConfig = { ...defaultConfig, ...config };
    return axios.delete(`${API_PATH}${url}`, finalConfig);
  };

  putFile = (url, data, config = defaultConfig) => {
    const finalConfig = { ...defaultConfig, ...config };
    delete finalConfig.headers.Authorization;
    return axios.put(`${url}`, data, finalConfig);
  };
}

const api = new API();

export default api;
